import React, { useMemo, useState } from 'react';
import { styles } from '../../config/styles';
import { formatCurrency, formatComitenteForDisplay } from '../../utils/formatters';
import { formatDate } from '../../utils/dateHelpers';
import { groupCajaPorComercialCliente } from '../../utils/licitacionesCalculator';
import { marcarCajaGrupo } from '../../services/licitacionesService';
import { useSaldosClientes } from '../../hooks/useSaldosClientes';
import { useFechaHoy } from '../../hooks/useFechaHoy';
import { ROLES } from '../../constants/roles';
import StackedTable from '../common/StackedTable';

const CAJA_STACK = { title: 'Cliente' };

const bucketToMonedaLabel = (bucket) => (bucket === 'ARS' ? 'Pesos' : 'USD Mep');

/**
 * Seguimiento de caja de una licitación: agrupa las órdenes por comercial y
 * cliente x moneda, muestra el monto a fondear (adjudicado o estimado) con el
 * saldo actual como referencia, y permite marcar "caja hecha" por grupo.
 * Solo el comercial dueño del cliente (o un Admin) puede marcar.
 *
 * Desde la fecha de caja, Control de Saldos ya descuenta lo adjudicado: el saldo de
 * referencia lo excluye para que se pueda comparar contra el monto a fondear sin
 * restarlo dos veces.
 */
const CajaSeguimientoPanel = ({ licitacion, ordenes, userRole, userName, userId }) => {
  const { getSaldoComitente } = useSaldosClientes();
  const [savingKey, setSavingKey] = useState(null);

  const isAdmin = userRole === ROLES.ADMIN || userRole === ROLES.SUPER_ADMIN;

  const especiesById = useMemo(() => {
    const map = {};
    (licitacion?.especies || []).forEach(esp => { map[esp.id] = esp; });
    return map;
  }, [licitacion]);

  const secciones = useMemo(
    () => groupCajaPorComercialCliente(ordenes || [], especiesById),
    [ordenes, especiesById]
  );

  const progreso = useMemo(() => {
    const grupos = secciones.flatMap(s => s.grupos);
    return { hecha: grupos.filter(g => g.cajaHecha).length, total: grupos.length };
  }, [secciones]);

  const hoyIso = useFechaHoy();
  const esDiaDeCaja = licitacion?.fechaCaja && hoyIso >= licitacion.fechaCaja
    && licitacion.estado !== 'Liquidada';

  const canMarcar = (grupo) => grupo.comercial === userName || isAdmin;

  const handleToggleCaja = async (grupo) => {
    if (!canMarcar(grupo) || savingKey) return;
    setSavingKey(`${grupo.comercial}|${grupo.key}`);
    try {
      await marcarCajaGrupo(grupo.ordenes, !grupo.cajaHecha, { userId, userName });
    } catch (error) {
      console.error('Error marcando la caja del grupo:', error);
    } finally {
      setSavingKey(null);
    }
  };

  if (progreso.total === 0) {
    return <p style={{ color: '#6B7281', padding: '1rem 0' }}>No hay órdenes activas para hacer seguimiento de caja.</p>;
  }

  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '0.85rem 1.25rem', marginBottom: '1.25rem', borderRadius: '0.5rem',
        backgroundColor: esDiaDeCaja && progreso.hecha < progreso.total ? styles.colors.red : '#FFFFFF',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
      }}>
        <div>
          <span style={{ fontWeight: 700, color: styles.colors.primary, fontSize: '1rem' }}>
            Caja hecha: {progreso.hecha} de {progreso.total} clientes
          </span>
          {esDiaDeCaja && progreso.hecha < progreso.total && (
            <span style={{ marginLeft: '1rem', color: styles.colors.redText, fontWeight: 600, fontSize: '0.875rem' }}>
              ¡Hoy es día de caja! Liquidación: {formatDate(`${licitacion.fechaLiquidacion}T00:00`)}
            </span>
          )}
        </div>
        <span style={{ fontSize: '0.8rem', color: '#6B7281' }}>
          Fecha de caja: {formatDate(`${licitacion.fechaCaja}T00:00`)}
        </span>
      </div>

      {secciones.map(seccion => (
        <div key={seccion.comercial} style={{ marginBottom: '1.5rem' }}>
          <h3 style={{
            fontFamily: "'Raleway', sans-serif", fontSize: '0.9rem', fontWeight: 700,
            color: styles.colors.primary, textTransform: 'uppercase', letterSpacing: '0.04em',
            margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center', gap: '0.6rem',
          }}>
            {seccion.comercial}
            <span style={{ fontSize: '0.75rem', fontWeight: 500, color: '#6B7281', textTransform: 'none' }}>
              ({seccion.grupos.filter(g => g.cajaHecha).length}/{seccion.grupos.length} con caja)
            </span>
            {seccion.comercial === userName && (
              <span style={{
                fontSize: '0.65rem', fontWeight: 700, backgroundColor: styles.colors.primary,
                color: '#FFFFFF', padding: '0.1rem 0.5rem', borderRadius: '10px', textTransform: 'none',
              }}>Tus clientes</span>
            )}
          </h3>
          <div style={{ ...styles.tableContainer, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)' }}>
            <StackedTable stack={CAJA_STACK} style={{ ...styles.table, minWidth: '850px' }}>
              <thead>
                <tr>
                  <th style={styles.th}>Caja</th>
                  <th style={styles.th}>Comitente</th>
                  <th style={styles.th}>Cliente</th>
                  <th style={styles.th}>Moneda</th>
                  <th style={styles.th}>Monto a Fondear</th>
                  <th style={styles.th}>Saldo Actual (ref.)</th>
                  <th style={styles.th}>Marcada por</th>
                </tr>
              </thead>
              <tbody>
                {seccion.grupos.map(grupo => {
                  const saldo = getSaldoComitente(
                    grupo.nroComitente,
                    bucketToMonedaLabel(grupo.bucket),
                    { excludeLicitacionId: licitacion?.id },
                  );
                  const habilitado = canMarcar(grupo);
                  const guardando = savingKey === `${grupo.comercial}|${grupo.key}`;
                  return (
                    <tr key={grupo.key} style={{ backgroundColor: grupo.cajaHecha ? styles.colors.green : 'transparent' }}>
                      <td style={styles.td}>
                        <input
                          type="checkbox"
                          checked={grupo.cajaHecha}
                          onChange={() => handleToggleCaja(grupo)}
                          disabled={!habilitado || guardando}
                          style={{ cursor: habilitado ? 'pointer' : 'not-allowed', width: '16px', height: '16px' }}
                          title={habilitado ? 'Marcar caja hecha' : `Solo ${grupo.comercial} (o un Admin) puede marcar este cliente`}
                        />
                      </td>
                      <td style={styles.td}>{formatComitenteForDisplay(grupo.nroComitente)}</td>
                      <td style={{ ...styles.td, textAlign: 'left' }}>{grupo.cliente}</td>
                      <td style={styles.td}>{grupo.bucket}</td>
                      <td style={{ ...styles.td, fontWeight: 600 }}>
                        {grupo.sinTipoCambio ? (
                          <span
                            style={{ color: '#B45309' }}
                            title="Hay órdenes de una especie en USD que se integra en pesos y todavía no tiene tipo de cambio cargado. Editá la licitación para cargarlo."
                          >
                            Falta TC
                          </span>
                        ) : (
                          <>
                            {formatCurrency(grupo.monto, grupo.bucket === 'ARS' ? 'Pesos' : 'USD')}
                            {grupo.esEstimado && (
                              <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', color: '#B45309', fontWeight: 500 }}>(estimado)</span>
                            )}
                          </>
                        )}
                      </td>
                      <td style={{
                        ...styles.td,
                        color: saldo?.tieneSnapshot
                          ? (saldo.saldoProyectado < 0 ? styles.colors.redText : '#065F46')
                          : '#9CA3AF',
                      }}>
                        {saldo?.tieneSnapshot
                          ? formatCurrency(saldo.saldoProyectado, grupo.bucket === 'ARS' ? 'Pesos' : 'USD')
                          : 'Sin snapshot'}
                      </td>
                      <td style={{ ...styles.td, fontSize: '0.8rem', color: '#6B7281' }}>
                        {grupo.cajaHecha && grupo.cajaMarcadaPorNombre
                          ? `${grupo.cajaMarcadaPorNombre}${grupo.cajaMarcadaAt ? ` — ${formatDate(grupo.cajaMarcadaAt)}` : ''}`
                          : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </StackedTable>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CajaSeguimientoPanel;
